import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { Alert, AlertTitle, Box, Chip, Step, StepContent, StepLabel, Typography } from "@mui/material";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import StatusChip from "server/components/StatusChip";
import {
  NacidFailureClassChoices,
  NacidFailureClassColorMap,
  NacidFlowStepStatusChoices,
  NacidFlowStepStatusColorMap,
  NacidProbeOutcomeChoices,
  NacidProbeOutcomeColorMap,
} from "utils/choices";
import { NacidFlowStepStatus, NacidProbeOutcome } from "utils/constants";
import RedoStepDialog from "./RedoStepDialog";
import ResolveStepEanDialog from "./ResolveStepEanDialog";
import StepRequests from "./StepRequests";

// The icon carries the status at a glance — the whole point of the spine is that a halted step is findable
// without reading anything.
const STEP_ICONS = {
  [NacidFlowStepStatus.Pending]: { Icon: RadioButtonUncheckedIcon, color: "disabled" },
  [NacidFlowStepStatus.InProgress]: { Icon: PlayCircleIcon, color: "primary" },
  [NacidFlowStepStatus.Succeeded]: { Icon: CheckCircleIcon, color: "success" },
  [NacidFlowStepStatus.SkippedAlreadyApplied]: { Icon: SkipNextIcon, color: "success" },
  [NacidFlowStepStatus.WaitingForRetry]: { Icon: ScheduleIcon, color: "info" },
  [NacidFlowStepStatus.WaitingForManualAction]: { Icon: PauseCircleIcon, color: "warning" },
  [NacidFlowStepStatus.Failed]: { Icon: ErrorIcon, color: "error" },
  [NacidFlowStepStatus.Cancelled]: { Icon: CancelIcon, color: "disabled" },
};

const StepStatusIcon = ({ status }) => {
  const { Icon, color } = STEP_ICONS[status] ?? STEP_ICONS[NacidFlowStepStatus.Pending];
  return <Icon color={color} />;
};

const formatDateTime = value => (value ? new Date(value).toLocaleString("bg-BG") : "—");

const Meta = ({ label, children }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
    >
      {label}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const FlowStep = ({ step, flowId }) => {
  // Parked and failed steps are the ones an operator can hand-resolve; anything else can only be re-issued.
  const isResolvable =
    step.status === NacidFlowStepStatus.WaitingForManualAction || step.status === NacidFlowStepStatus.Failed;

  return (
    <Step
      expanded
      completed={
        step.status === NacidFlowStepStatus.Succeeded ||
        step.status === NacidFlowStepStatus.SkippedAlreadyApplied
      }
    >
      <StepLabel StepIconComponent={() => <StepStatusIcon status={step.status} />}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {step.order}. {step.naturalKeyDescription || "—"}
          </Typography>
          <StatusChip
            status={step.status}
            choices={NacidFlowStepStatusChoices}
            colorMap={NacidFlowStepStatusColorMap}
          />
          {/* Revision only ever moves when a human deliberately re-issued the step. */}
          {step.revision > 1 ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={`Ревизия ${step.revision}`}
            />
          ) : null}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontFamily: "monospace", color: "text.secondary" }}
        >
          {step.operationName ?? step.operationId}
          {step.operationCode ? ` · код ${step.operationCode}` : ""}
        </Typography>
      </StepLabel>

      <StepContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 2,
          }}
        >
          <Meta label="Опити">
            {step.attemptCount} / {step.maxAttempts}
          </Meta>
          <Meta label="Последен опит">{formatDateTime(step.lastAttemptOn)}</Meta>
          <Meta label="Следващ опит">{formatDateTime(step.nextAttemptOn)}</Meta>
          <Meta label="Завършена">{formatDateTime(step.completedOn)}</Meta>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {step.failureClass ? (
            <StatusChip
              status={step.failureClass}
              choices={NacidFailureClassChoices}
              colorMap={NacidFailureClassColorMap}
            />
          ) : null}
          {step.probeOutcome && step.probeOutcome !== NacidProbeOutcome.NotProbed ? (
            <StatusChip
              status={step.probeOutcome}
              choices={NacidProbeOutcomeChoices}
              colorMap={NacidProbeOutcomeColorMap}
            />
          ) : null}
          {step.requiresProbeBeforeRetry ? (
            <Chip
              size="small"
              variant="outlined"
              label="Проверка преди следващ опит"
            />
          ) : null}
        </Box>

        {/* The operator's actual instruction — it gets the most visual weight on the step. */}
        {step.manualReasonCode ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
          >
            <AlertTitle>Изисква ръчна намеса</AlertTitle>
            {step.manualInstructionBg || step.resultMessage}
            <Typography
              variant="caption"
              display="block"
              sx={{ fontFamily: "monospace", mt: 1 }}
            >
              {step.manualReasonCode}
            </Typography>
          </Alert>
        ) : null}

        {step.resultCode || step.resultMessage ? (
          <Alert
            severity={step.status === NacidFlowStepStatus.Failed ? "error" : "info"}
            sx={{ mb: 2 }}
          >
            {step.resultMessage}
            {step.resultCode ? (
              <Typography
                variant="caption"
                display="block"
                sx={{ fontFamily: "monospace", mt: 1 }}
              >
                {step.resultCode}
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        {step.resolutionNote ? (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
          >
            <AlertTitle>Разрешена ръчно</AlertTitle>
            {step.resolutionNote}
          </Alert>
        ) : null}

        <StepRequests
          flowId={flowId}
          step={step}
        />

        <SecuredComponent permission={permissions.canManageNacidFlows}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {isResolvable ? (
              <ResolveStepEanDialog
                flowId={flowId}
                stepId={step.id}
              />
            ) : null}
            <RedoStepDialog
              flowId={flowId}
              step={step}
            />
          </Box>
        </SecuredComponent>
      </StepContent>
    </Step>
  );
};

export default FlowStep;
